import mapboxgl from 'mapbox-gl'
import gsap from 'gsap'

export default class Map {
	static token = 'pk.eyJ1Ijoic3RhcnRkaWciLCJhIjoiY21vODFoMGxnMDllNTJwc2F5a21jYzkxdSJ9.fa_NyBvccfXVTKEFj6p3JA'

	static stateLabels = {
		WA: 'WA',
		NT: 'NT',
		SA: 'SA',
		QLD: 'QLD',
		NSW: 'NSW',
		VIC: 'VIC',
		TAS: 'TAS',
		ACT: 'ACT',
	}

	// Display order
	static stateOrder = ['WA', 'NT', 'SA', 'QLD', 'NSW', 'VIC', 'TAS', 'ACT']

	static stateBounds = {
		WA: [112.0, 129.0, -35.5, -13.5],
		NT: [129.0, 138.0, -26.0, -10.5],
		SA: [129.0, 141.0, -38.1, -26.0],
		QLD: [138.0, 154.0, -29.0, -10.0],
		NSW: [141.0, 154.0, -37.5, -28.0],
		VIC: [141.0, 150.0, -39.2, -34.0],
		TAS: [144.0, 149.0, -43.7, -39.5],
		ACT: [148.7, 149.4, -35.9, -35.1],
	}

	static getStateFromCoords(lng, lat) {
		for (const state of ['ACT', 'TAS', 'VIC', 'NSW', 'QLD', 'SA', 'NT', 'WA']) {
			const [minLng, maxLng, minLat, maxLat] = Map.stateBounds[state]
			if (lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat) {
				return state
			}
		}
		return ''
	}

	constructor() {
		this.container = document.querySelector('[data-map-block]')
		if (!this.container) return

		this.mapContainer = this.container.querySelector('#map')
		if (!this.mapContainer) return

		this.teases = this.container.querySelectorAll('[data-project-tease]')
		if (!this.teases.length > 0) return

		this.buttons = this.container.querySelectorAll('[data-map-term-slug]')
		this.stateSelect = this.container.querySelector('[data-map-state-filter]')
		this.activeFilter = null
		this.activeState = null
		this.markers = []

		this.initMap()
		this.createMarkers()
		this.populateStateDropdown()
		this.applyFilters()
		this.bindButtons()
		this.bindStateFilter()
	}

	initMap() {
		mapboxgl.accessToken = Map.token

		this.map = new mapboxgl.Map({
			container: this.mapContainer,
			style: 'mapbox://styles/mapbox/light-v11',
			center: [115.8613, -31.9523],
			zoom: 10,
			scrollZoom: false,
			dragRotate: false,
			touchZoomRotate: false,
			doubleClickZoom: false,
			keyboard: false,
			minZoom: 4,
			maxBounds: [
				[112.0, -45.0],
				[154.0, -10.0],
			],
		})

		this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')

		this.map.on('zoom', () => {
			const zoom = this.map.getZoom()
			const size = Math.max(6, Math.min(24, ((zoom - 4) / 6) * 24))
			this.markers.forEach(({ el }) => {
				el.style.width = `${size}px`
				el.style.height = `${size}px`
			})
		})
	}

	createMarkers() {
		this.teases.forEach(tease => {
			const lng = parseFloat(tease.dataset.x)
			const lat = parseFloat(tease.dataset.y)
			const availability = tease.dataset.teaseAvailability
			const state = Map.getStateFromCoords(lng, lat)

			tease.dataset.teaseState = state

			if (isNaN(lng) || isNaN(lat) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return

			const el = document.createElement('div')
			el.className = 'map-marker'

			const title = tease.dataset.teaseTitle || ''
			const subtitle = tease.dataset.teaseSubtitle || ''
			const link = tease.dataset.teaseLink || '#'

			const popup = new mapboxgl.Popup({
				offset: 12,
				closeButton: false,
				closeOnClick: false,
				className: 'map-popup',
			}).setHTML(`
				<a href="${link}" class="map-popup-inner">
					<strong>${title}</strong>
					${subtitle ? `<span>${subtitle}</span>` : ''}
				</a>
			`)

			el.addEventListener('mouseenter', () => marker.togglePopup())
			el.addEventListener('mouseleave', () => marker.togglePopup())
			el.addEventListener('click', () => {
				window.location.href = link
			})

			const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]).setPopup(popup).addTo(this.map)

			this.markers.push({ marker, el, tease, availability, state })
		})
	}

	populateStateDropdown() {
		this.dropdown = this.container.querySelector('[data-map-state-dropdown]')
		if (!this.dropdown) return

		this.dropdownToggle = this.dropdown.querySelector('[data-map-state-toggle]')
		this.dropdownLabel = this.dropdown.querySelector('[data-map-state-label]')
		this.dropdownOptions = this.dropdown.querySelector('[data-map-state-options]')
		this.dropdownArrow = this.dropdown.querySelector('[data-map-state-arrow]')

		const activeStates = new Set(this.markers.map(m => m.state).filter(Boolean))

		// "All States" option
		this.addDropdownOption('', 'All States')

		Map.stateOrder.forEach(code => {
			if (!activeStates.has(code)) return
			this.addDropdownOption(code, Map.stateLabels[code])
		})

		this.activeState = 'WA'
		this.dropdownLabel.textContent = 'WA'

		this.dropdownToggle.style.minWidth = `${this.dropdownToggle.offsetWidth}px`
	}

	addDropdownOption(value, label) {
		const button = document.createElement('button')
		button.className =
			'button-font w-full whitespace-nowrap px-12 py-3 text-left duration-150 ease-in-out hover:bg-black hover:text-white'
		button.dataset.stateValue = value
		button.textContent = label
		this.dropdownOptions.appendChild(button)
	}

	bindStateFilter() {
		if (!this.dropdown) return
		this.isOpen = false

		this.dropdownToggle.addEventListener('click', () => {
			if (this.isOpen) {
				this.closeDropdown()
			} else {
				this.openDropdown()
			}
		})

		this.dropdownOptions.addEventListener('click', e => {
			const button = e.target.closest('[data-state-value]')
			if (!button) return

			const value = button.dataset.stateValue
			this.activeState = value || null
			this.dropdownLabel.textContent = button.textContent
			this.closeDropdown()
			this.applyFilters()
		})

		document.addEventListener('click', e => {
			if (!this.dropdown.contains(e.target)) {
				this.closeDropdown()
			}
		})
	}

	openDropdown() {
		if (this.isOpen) return
		this.isOpen = true

		this.dropdownToggle.classList.add('border-b-0')
		this.dropdownOptions.classList.remove('hidden')
		this.dropdownOptions.classList.add('flex')

		const height = this.dropdownOptions.scrollHeight

		gsap.fromTo(
			this.dropdownOptions,
			{ height: 0, overflow: 'hidden' },
			{
				height,
				duration: 0.3,
				ease: 'power3.out',
				onComplete: () => {
					this.dropdownOptions.style.height = 'auto'
					this.dropdownOptions.style.overflow = ''
				},
			}
		)

		gsap.to(this.dropdownArrow, { rotation: 0, duration: 0.15, ease: 'power2.out' })
	}

	closeDropdown() {
		if (!this.isOpen) return
		this.isOpen = false

		const height = this.dropdownOptions.scrollHeight

		gsap.fromTo(
			this.dropdownOptions,
			{ height, overflow: 'hidden' },
			{
				height: 0,
				duration: 0.15,
				ease: 'power3.in',
				onComplete: () => {
					this.dropdownOptions.classList.add('hidden')
					this.dropdownOptions.classList.remove('flex')
					this.dropdownOptions.style.height = ''
					this.dropdownOptions.style.overflow = ''
					this.dropdownToggle.classList.remove('border-b-0')
				},
			}
		)

		gsap.to(this.dropdownArrow, { rotation: 180, duration: 0.15, ease: 'power2.in' })
	}

	getVisibleMarkers() {
		return this.markers.filter(({ marker }) => marker.getElement().style.display !== 'none')
	}

	fitToMarkers() {
		const visible = this.getVisibleMarkers()
		if (visible.length === 0) return

		if (visible.length === 1) {
			const lngLat = visible[0].marker.getLngLat()
			this.map.flyTo({ center: lngLat, zoom: 13, duration: 600 })
			return
		}

		const bounds = new mapboxgl.LngLatBounds()
		visible.forEach(({ marker }) => bounds.extend(marker.getLngLat()))
		this.map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 600 })
	}

	bindButtons() {
		this.buttons.forEach(button => {
			button.addEventListener('click', () => {
				const slug = button.dataset.mapTermSlug

				if (this.activeFilter === slug) {
					this.activeFilter = null
					this.buttons.forEach(b => b.classList.remove('active'))
				} else {
					this.activeFilter = slug
					this.buttons.forEach(b => b.classList.remove('active'))
					button.classList.add('active')
				}

				this.applyFilters()
			})
		})
	}

	applyFilters() {
		this.teases.forEach(tease => {
			const availability = tease.dataset.teaseAvailability || ''
			const matchesTerm = !this.activeFilter || availability.split(' ').includes(this.activeFilter)
			const matchesState = !this.activeState || tease.dataset.teaseState === this.activeState
			tease.style.display = matchesTerm && matchesState ? '' : 'none'

			const tagEls = tease.querySelectorAll('[data-availability-tag]')
			tagEls.forEach(tag => {
				if (!this.activeFilter) {
					tag.style.display = ''
				} else {
					tag.style.display = tag.dataset.availabilityTag === this.activeFilter ? '' : 'none'
				}
			})

			const tagContainer = tease.querySelector('[data-availability-tags]')
			if (tagContainer) {
				const visibleTags = [...tagContainer.querySelectorAll('[data-availability-tag]')].filter(
					tag => tag.style.display !== 'none'
				)

				tagContainer.querySelectorAll('.tag-separator').forEach(s => s.remove())

				visibleTags.forEach((tag, i) => {
					if (i < visibleTags.length - 1) {
						const sep = document.createElement('span')
						sep.className = 'tag-separator'
						sep.textContent = ' • '
						tag.after(sep)
					}
				})
			}
		})

		this.markers.forEach(({ marker, availability, state }) => {
			const matchesTerm = !this.activeFilter || (availability || '').split(' ').includes(this.activeFilter)
			const matchesState = !this.activeState || state === this.activeState
			marker.getElement().style.display = matchesTerm && matchesState ? '' : 'none'
		})

		this.fitToMarkers()
	}
}
